import { useQueryClient } from '@tanstack/react-query';
import { useBunja } from 'bunja/react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import React, { useEffect, useRef, useState } from 'react';
import { useClickAway } from 'react-use';

import { deleteCommunityFeedItemApi, postCommunityFeedReactionApi } from '@flipster/apis/v1/community';
import { CommunityFeedItemWithSymbol, FeedReactionInfo, FeedReactionType } from '@flipster/apis/v1/community/types';
import { FormattedMessage } from '@flipster/i18n';
import { Link } from '@flipster/i18n/routing';
import { useOverlay } from '@flipster/overlay';
import { Modal } from '@flipster/react/design-system-legacy/modal';
import { Badge } from '@flipster/react/design-system/badge';
import { Button } from '@flipster/react/design-system/button';
import { createDropdownSelect } from '@flipster/react/design-system/dropdown/dropdown-select';
import { snackbar } from '@flipster/react/design-system/snackbar';
import { TextButton } from '@flipster/react/design-system/text-button';
import { Datetime } from '@flipster/react/design-utils/datetime';
import EditIcon from '@flipster/react/icon/generated/EditIcon';
import EllipsisVerticalIcon from '@flipster/react/icon/generated/EllipsisVerticalIcon';
import FlagIcon from '@flipster/react/icon/generated/FlagIcon';
import TranslateIcon from '@flipster/react/icon/generated/TranslateIcon';
import TrashIcon from '@flipster/react/icon/generated/TrashIcon';
import { useBreakpoint } from '@flipster/react/misc/responsive';
import { Styles, css } from '@flipster/styled-system/css';
import { hstack, square, stack } from '@flipster/styled-system/patterns';
import { Token, token } from '@flipster/styled-system/tokens';
import { on } from '@flipster/utils/dom-event-listener';
import { getErrorMessage } from '@flipster/utils/error';
import { map } from '@flipster/utils/map';
import segment from '@flipster/utils/segment';
import { CommunityEvent, FeedEventLocation } from '@flipster/utils/segment/events/community';

import { SymbolDisplay } from '../../../misc/display';
import { profileFeedQueryKey } from '../../../profile-page/section/feed/bunja';
import { CopyMasterBadge } from '../../copy-trading/badge';
import { getSegmentPositionSide } from '../../position/segment';
import { ProfileRankBadge } from '../../profile-rank-badge';
import { feedCardBackgroundColorVarKey } from '../constant';
import { getSegmentPositionStatus, getSegmentRealizedPnl, getSegmentRealizedPnlPct } from '../event';
import { CommunityFeedQueryKey, InsightsFeedQueryKey } from '../post-and-edit/bunja';
import { FeedPostEditForm } from '../post-and-edit/form';
import { ReportPostModal } from '../report-post-modal';
import { UserTierAccountIcon } from '../user-tier-account-icon';
import { feedCardBunja } from './bunja';
import { FeedPositionCard } from './position-card';
import { reactionTypes, reactions } from './reaction/constants';
import { ReactionButton } from './reaction/reaction-button';

interface FeedCardProps {
  css?: Styles;
  backgroundColor?: Token;
  ref?: React.Ref<HTMLDivElement>;
  withSymbolDescription?: boolean;
  onSymbolDescriptionClick?: (symbol: string) => void;
  onPositionCardClick?: (symbol: string) => void;
  onDropdownClick?: () => void;
  onEditClick?: () => void;
  onDeleteClick?: () => void;
  onReadMoreClick?: () => void;
  onEditComplete?: () => void;
  eventLocation: FeedEventLocation;
}
export const FeedCard = ({
  css: cssProps,
  backgroundColor = 'colors.background.default.level-0',
  ref,
  withSymbolDescription,
  onSymbolDescriptionClick,
  onPositionCardClick,
  onDropdownClick,
  onEditClick,
  onDeleteClick,
  onReadMoreClick,
  onEditComplete,
  eventLocation,
}: FeedCardProps) => {
  const t = useTranslations();
  const router = useRouter();
  const { isMobile } = useBreakpoint();
  const { item, isLoggedInAtom, statusAtom, showTranslatedAtom, getTranslation, isTranslationLoadingAtom } =
    useBunja(feedCardBunja);

  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const status = useAtomValue(statusAtom);
  const [showTranslated, setShowTranslated] = useAtom(showTranslatedAtom);
  const isTranslationLoading = useAtomValue(isTranslationLoadingAtom);

  const viewTranslation = item.textContent != null && !item.isMyFeed;
  // remove this if more action except report can be executed in dropdown
  const notMyFeedAndEmptyTextContent = !item.isMyFeed && !item.textContent;

  const showInfluencerBadge =
    (item.weeklyLeaderboardRank == null || item.weeklyLeaderboardRank > 100) && item.fromInfluencer;
  const showRankBadge = !showInfluencerBadge && item.weeklyLeaderboardRank != null;

  const feedReactions = Object.fromEntries(item.reactions.map((r) => [r.type, r])) as Record<
    FeedReactionType,
    FeedReactionInfo | undefined
  >;

  return (
    <div
      className={css(stack.raw({ gap: 12 }), cssProps)}
      ref={ref}
      style={
        {
          backgroundColor: token(backgroundColor),
          [feedCardBackgroundColorVarKey]: token(backgroundColor),
        } as Record<string, string>
      }
    >
      <div className={stack({ gap: 16 })}>
        <div className={hstack({ justifyContent: 'space-between' })}>
          <div className={hstack({ gap: 8 })}>
            <UserTierAccountIcon vipTier={item.vipTier} me={item.isMyFeed} profileImageUrl={item.profileImageUrl} />
            <div className={stack()}>
              <div className={hstack({ justifyContent: 'space-between' })}>
                <div className={hstack({ gap: 4 })}>
                  <Link
                    href={isLoggedIn ? `/user/profile/${item.profileId}` : '/login'}
                    onClick={() => {
                      segment.track(CommunityEvent.FeedPageUserProfileClick, {
                        profile_id: item.profileId,
                        profile_ranking: item.weeklyLeaderboardRank?.toString(),
                        profile_username: item.nickname,
                        symbol: item.symbol,
                        leverage: map(item.positionCard, (card) => card.initLeverage),
                        side: map(item.positionCard, (card) => getSegmentPositionSide(card.side)),
                        position_status: getSegmentPositionStatus(item.positionCardType),
                        realized_pnl: getSegmentRealizedPnl(item),
                        realized_pnl_percentage: getSegmentRealizedPnlPct(item),
                        is_influencer: item.fromInfluencer,
                      });
                    }}
                  >
                    <p className={css({ textStyle: 'Text/XS/Strong', color: 'content.default.level-3' })}>
                      {item.nickname}
                    </p>
                  </Link>
                  <div
                    className={hstack({
                      gap: 4,
                      tabletDown: { '& > :nth-child(n + 2)': { display: 'none' } },
                      tablet: { '& > :nth-child(n + 4)': { display: 'none' } },
                    })}
                  >
                    {item.fromCopyTradeMaster && <CopyMasterBadge />}
                    {showRankBadge && <ProfileRankBadge rank={item.weeklyLeaderboardRank!} />}
                    {showInfluencerBadge && (
                      <Badge type='tint' color='promotion'>
                        <FormattedMessage id='Autogen_common.r251126.s_Influencer' />
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
              <div className={hstack({ gap: 4 })}>
                <Datetime
                  format="MMM dd 'at' HH:mm zz"
                  value={item.createdAt}
                  isUTC
                  css={{ textStyle: 'Text/XS/Regular', color: 'content.default.level-1' }}
                />
                {withSymbolDescription && (
                  <button
                    className={css({ textStyle: 'Text/XS/Regular', color: 'content.default.level-1' })}
                    onClick={() => {
                      if (isMobile) return;
                      onSymbolDescriptionClick?.(item.symbol);
                      router.push(`/trade/perpetual/${item.symbol}?tab=social`);
                    }}
                  >
                    · From
                    <SymbolDisplay
                      symbol={item.symbol}
                      mainLabel={
                        <SymbolDisplay.Label
                          value='shortName'
                          css={{ textStyle: 'Text/S/Strong', color: 'content.default.level-3', mx: 4 }}
                        />
                      }
                    />
                    feed
                  </button>
                )}
              </div>
            </div>
          </div>
          <FeedCardDropdown
            disabled={!isLoggedIn || notMyFeedAndEmptyTextContent}
            onDropdownClick={onDropdownClick}
            onEditClick={onEditClick}
            onDeleteClick={onDeleteClick}
          />
        </div>
        {status === 'EDIT' ? (
          <EditFeedCard onComplete={onEditComplete} />
        ) : (
          <FeedContent onPositionCardClick={onPositionCardClick} onReadMoreClick={onReadMoreClick} />
        )}
      </div>
      <div className={hstack({ justifyContent: 'space-between' })}>
        <div className={hstack({ gap: 16, ml: -8 })}>
          {reactionTypes.map((type) => {
            const { Icon, color } = reactions[type];
            const feedReaction = feedReactions[type];
            if (!feedReaction) return;
            return (
              <ReactionButton
                // discard state if selected is changed
                key={type + feedReaction.isReactedByMe}
                Icon={Icon}
                color={color}
                count={feedReaction.count}
                selected={feedReaction.isReactedByMe}
                disabled={!isLoggedIn}
                onClick={async (remove: boolean) => {
                  try {
                    await postCommunityFeedReactionApi(item.symbol, item.feedId, { reactionType: type, remove });
                    segment.track(CommunityEvent.FeedPositionReactionClick, {
                      profile_id: item.profileId,
                      profile_ranking: item.weeklyLeaderboardRank?.toString(),
                      profile_username: item.nickname,
                      symbol: item.symbol,
                      leverage: map(item.positionCard, (card) => card.initLeverage),
                      side: map(item.positionCard, (card) => getSegmentPositionSide(card.side)),
                      position_status: getSegmentPositionStatus(item.positionCardType),
                      realized_pnl: getSegmentRealizedPnl(item),
                      realized_pnl_percentage: getSegmentRealizedPnlPct(item),
                      reaction: type,
                      event_location: eventLocation,
                    });
                  } catch (error) {
                    snackbar.error({ message: getErrorMessage(error) });
                  }
                }}
              />
            );
          })}
        </div>
        {viewTranslation &&
          (() => {
            if (isTranslationLoading) {
              return (
                <TextButton
                  disabled
                  size='small'
                  color='passive'
                  icon={<TranslateIcon />}
                  label={t('Autogen_common.r251126.s_Translating')}
                />
              );
            }
            if (showTranslated) {
              return (
                <TextButton
                  size='small'
                  color='passive'
                  icon={<TranslateIcon />}
                  label={t('Autogen_common.r251126.s_View_original')}
                  onClick={() => setShowTranslated(false)}
                />
              );
            }
            return (
              <TextButton
                disabled={!isLoggedIn}
                size='small'
                color='passive'
                icon={<TranslateIcon />}
                label={t('Common.r251201.View_translation')}
                onClick={getTranslation}
              />
            );
          })()}
      </div>
    </div>
  );
};

interface EditFeedCardProps {
  onComplete?: () => void;
}
const EditFeedCard = ({ onComplete }: EditFeedCardProps) => {
  const { statusAtom } = useBunja(feedCardBunja);
  const setStatus = useSetAtom(statusAtom);
  return (
    <FeedPostEditForm
      submitLabel='Update'
      onComplete={() => {
        onComplete?.();
        setStatus('VIEW');
      }}
      onCancel={() => setStatus('VIEW')}
    />
  );
};

interface FeedContentProps {
  onPositionCardClick?: (symbol: string) => void;
  onReadMoreClick?: () => void;
}
const FeedContent = ({ onPositionCardClick, onReadMoreClick }: FeedContentProps) => {
  const { item, translatedAtom, showTranslatedAtom } = useBunja(feedCardBunja);
  const translated = useAtomValue(translatedAtom);
  const showTranslated = useAtomValue(showTranslatedAtom);

  const textContentRef = useRef<HTMLParagraphElement>(null);

  const [isClamped, setIsClamped] = useState<boolean>(false);
  const [isCollapsed, setIsCollapsed] = useState(true);

  useEffect(() => {
    return updateClamp(), on(window, 'resize', updateClamp);
    function updateClamp() {
      const el = textContentRef.current;
      // do not calculate if it is clamped when expanded already
      if (!isCollapsed || el == null) return;
      setIsClamped(el.clientHeight < el.scrollHeight);
    }
  }, [isCollapsed, showTranslated, item]); // item should be included in the dependency array for handling edits

  return (
    <>
      <div className={css({ _empty: { display: 'none' } })}>
        {item.textContent && (
          <p
            className={css(
              {
                lineClamp: 4,
                textOverflow: 'ellipsis',
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word',
                wordBreak: 'break-word',
              },
              !isCollapsed && { lineClamp: 'inherit' },
            )}
            ref={textContentRef}
          >
            {showTranslated ? translated : item.textContent}
          </p>
        )}
        {isClamped && (
          <TextButton
            color='passive'
            label={isCollapsed ? 'Read more' : 'View less'}
            onClick={() => {
              onReadMoreClick?.();
              setIsCollapsed((v) => !v);
            }}
          />
        )}
      </div>
      {item.positionCard && <FeedPositionCard type='VIEW' item={item} onClick={onPositionCardClick} />}
    </>
  );
};

const Dropdown = createDropdownSelect();

interface FeedCardDropdownProps {
  disabled: boolean;
  onDropdownClick?: () => void;
  onEditClick?: () => void;
  onDeleteClick?: () => void;
}
const FeedCardDropdown = ({ disabled, onDropdownClick, onEditClick, onDeleteClick }: FeedCardDropdownProps) => {
  const t = useTranslations();
  const { item, statusAtom } = useBunja(feedCardBunja);
  const overlay = useOverlay();
  const menuRef = useRef<HTMLDivElement>(null);
  const [isMenuOpened, setIsMenuOpened] = useState(false);
  const setStatus = useSetAtom(statusAtom);
  useClickAway(menuRef, () => setIsMenuOpened(false));
  return (
    <Dropdown.Root placement='bottom-end'>
      <Dropdown.Trigger disabled={disabled}>
        <EllipsisVerticalIcon
          size={16}
          color={disabled ? token('colors.content.interaction.disabled') : token('colors.content.default.level-2')}
        />
      </Dropdown.Trigger>
      <Dropdown.Select>
        {item.isMyFeed ? (
          <>
            <Dropdown.Item
              icon={<EditIcon />}
              label={t('Autogen_common.r251126.s_Edit')}
              value='Edit'
              onClick={() => {
                onEditClick?.();
                setStatus('EDIT');
              }}
            />
            <Dropdown.Item
              icon={<TrashIcon />}
              label={t('Autogen_common.r251126.s_Delete')}
              value='Delete'
              onClick={() => {
                onDeleteClick?.();
                overlay.open(({ isOpen, onClose, unmount }) => (
                  <FeedDeleteModal isOpen={isOpen} onClose={onClose} item={item} onExit={unmount} />
                ));
              }}
            />
          </>
        ) : (
          <Dropdown.Item
            icon={<FlagIcon />}
            label={t('Autogen_common.r251126.s_Report')}
            value='Report'
            onClick={() => {
              overlay.open(({ isOpen, onClose, unmount }) => (
                <ReportPostModal
                  isOpen={isOpen}
                  onClose={onClose}
                  feedId={item.feedId}
                  symbol={item.symbol}
                  onExit={unmount}
                />
              ));
            }}
          />
        )}
      </Dropdown.Select>
    </Dropdown.Root>
  );
  return (
    <div className={css({ position: 'relative', alignSelf: 'self-start' })}>
      <button
        disabled={disabled}
        onClick={() => {
          onDropdownClick?.();
          setIsMenuOpened(true);
        }}
        className={square()}
      >
        <EllipsisVerticalIcon
          size={16}
          color={disabled ? token('colors.content.interaction.disabled') : token('colors.content.default.level-2')}
        />
      </button>
      {isMenuOpened && (
        <div
          ref={menuRef}
          className={stack({
            w: 120,
            rounded: 8,
            p: '4px 16px',
            position: 'absolute',
            right: 0,
            bottom: -8,
            transform: 'translateY(100%)',
            bg: 'background.default.level-4',
            zIndex: 1, // this fixes textarea click issue: https://prex.atlassian.net/browse/QA-17167
          })}
        >
          {item.isMyFeed ? (
            <>
              <button
                type='button'
                onClick={() => {
                  onEditClick?.();
                  setIsMenuOpened(false);
                  setStatus('EDIT');
                }}
                className={hstack({ gap: 8, py: 8, color: 'content.default.level-5', textStyle: 'Text/L/Regular' })}
              >
                <EditIcon size={16} />
                Edit
              </button>
              <button
                type='button'
                onClick={() => {
                  onDeleteClick?.();
                  setIsMenuOpened(false);
                  overlay.open(({ isOpen, onClose, unmount }) => (
                    <FeedDeleteModal isOpen={isOpen} onClose={onClose} item={item} onExit={unmount} />
                  ));
                }}
                className={hstack({ gap: 8, py: 8, color: 'content.default.level-5', textStyle: 'Text/L/Regular' })}
              >
                <TrashIcon size={16} />
                Delete
              </button>
            </>
          ) : (
            <button
              type='button'
              onClick={() => {
                setIsMenuOpened(false);
                overlay.open(({ isOpen, onClose, unmount }) => (
                  <ReportPostModal
                    isOpen={isOpen}
                    onClose={onClose}
                    feedId={item.feedId}
                    symbol={item.symbol}
                    onExit={unmount}
                  />
                ));
              }}
              className={hstack({ gap: 8, py: 8, color: 'content.default.level-5', textStyle: 'Text/L/Regular' })}
            >
              <FlagIcon size={16} />
              Report
            </button>
          )}
        </div>
      )}
    </div>
  );
};

interface FeedDeleteModalProps {
  item: CommunityFeedItemWithSymbol;
  isOpen: boolean;
  onClose: () => void;
  onExit: () => void;
}
const FeedDeleteModal = ({ item, isOpen, onClose, onExit }: FeedDeleteModalProps) => {
  const t = useTranslations();
  const queryClient = useQueryClient();
  return (
    <Modal isOpen={isOpen} onExit={onExit}>
      <Modal.Close onClose={onClose} />
      <Modal.Content css={{ textStyle: 'Title/M', color: 'content.default.level-5' }}>
        <FormattedMessage id='Autogen_common.r251126.s_Are_you_sure_you_want_to_delete_this_post' />
      </Modal.Content>
      <Modal.Footer>
        <Button
          hierarchy='secondary'
          size='large'
          color='neutral'
          onClick={() => {
            segment.track(CommunityEvent.FeedPositionCancelClick, {
              profile_id: item.profileId,
              profile_ranking: item.weeklyLeaderboardRank?.toString(),
              profile_username: item.nickname,
              symbol: item.symbol,
              leverage: map(item.positionCard, (card) => card.initLeverage),
              side: map(item.positionCard, (card) => getSegmentPositionSide(card.side)),
              position_status: getSegmentPositionStatus(item.positionCardType),
              realized_pnl: getSegmentRealizedPnl(item),
              realized_pnl_percentage: getSegmentRealizedPnlPct(item),
            });
            onClose();
          }}
          label={t('Common.r250401.Cancel')}
        />
        <Button
          size='large'
          withSpinner
          onClick={async () => {
            segment.track(CommunityEvent.FeedPositionDeleteClick, {
              profile_id: item.profileId,
              profile_ranking: item.weeklyLeaderboardRank?.toString(),
              profile_username: item.nickname,
              symbol: item.symbol,
              leverage: map(item.positionCard, (card) => card.initLeverage),
              side: map(item.positionCard, (card) => getSegmentPositionSide(card.side)),
              position_status: getSegmentPositionStatus(item.positionCardType),
              realized_pnl: getSegmentRealizedPnl(item),
              realized_pnl_percentage: getSegmentRealizedPnlPct(item),
            });
            try {
              await deleteCommunityFeedItemApi(item.symbol, item.feedId);
              await Promise.all([
                queryClient.invalidateQueries({ queryKey: [CommunityFeedQueryKey] }),
                queryClient.invalidateQueries({ queryKey: [InsightsFeedQueryKey] }),
                queryClient.invalidateQueries({ queryKey: [profileFeedQueryKey] }),
              ]);
              snackbar.success({ message: 'Your post has been deleted' });
            } catch (error) {
              snackbar.error({ message: getErrorMessage(error) });
            } finally {
              onClose();
            }
          }}
          label={t('Autogen_common.r251126.s_Delete_post')}
        />
      </Modal.Footer>
    </Modal>
  );
};
